import { Component, EventEmitter, Input, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { gql } from 'apollo-angular';
import { OuiDialogService } from 'omnium-ui/dialog';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { ExcelExportComponent } from 'projects/box-lib/src/lib/components/excel-export/excel-export.component';
import { NotifyMailConsultantComponent } from 'projects/box-lib/src/lib/components/operations-dashboard/notify-mail-consultant/notify-mail-consultant.component';
import { OperationsDispatcherComponent } from 'projects/box-lib/src/lib/components/operations-dashboard/operations-dispatcher/operations-dispatcher.component';
import {
  OperationSelectionAction,
  OperationSelectionActionConfig,
  OperationSelectionActionType,
  OperationsTableComponent,
} from 'projects/box-lib/src/lib/components/operations-dashboard/operations-table/operations-table.component';
import { QueryManagerService } from 'projects/box-lib/src/lib/services/queryManagerService';
import { UserParametersService } from 'projects/box-lib/src/lib/services/user-parameters.service';
import { DashboardFilterEnum } from '../../models/dashboardFilterEnum';
import {
  BackOfficeMember,
  EnveloppeProduit,
  NatureOperation,
  Operation,
  OperationFilterInput,
  OperationSortInput,
  OperationsPaginatedCollectionSegment,
  OperationStateTransitionTrigger,
  Produit,
  SortEnumType,
} from '../../models/generated/graphql';
import { emailCheckFragment, operationDispatchedFragment } from '../../models/graphqlFragments';
import { ConfirmationSimpleComponent } from '../confirmation-simple/confirmation-simple.component';
import { DEFAULT_PAGINATION_PARAMS, PaginationParams } from '../paginated-table/paginated-table.component';
import { DeleteDraftConfirmComponent } from './delete-draft-confirm/delete-draft-confirm.component';
import { OperationDashboardColumn } from './operations-dashboard-columns';
import { RefuseCancelOperationModalComponent } from 'projects/box-lib/src/lib/components/refuse-cancel-operation-modal/refuse-cancel-operation-modal.component';

const ALLOPERATIONTABLE = gql`
  query operationsPaginated($skip: Int, $take: Int, $filter: OperationFilterInput, $order: [OperationSortInput!]) {
    operationsPaginated(skip: $skip, take: $take, where: $filter, order: $order) {
      totalCount
      pageInfo {
        hasNextPage
      }
      items {
        id
        statutId
        montant
        commentaireGestionnaire
        gestionnaire {
          displayName
        }
        statut {
          id
          consultantLibelle
          backOfficeLibelle
        }
        produitPreset {
          nom
          nomTechniqueProduitBox
          habilitationCode
          habilitationLibelle
          enveloppeCode
          enveloppeLibelle
        }
        dateModification
        dateDeclaration
        consultantPreset {
          nom
          prenom
          habilitationCodes
        }
        donneesSpecifiques
        operationConfig {
          natureOperation {
            libelle
          }
        }
        coInvestisseurPreset {
          displayName
        }
        investisseurPreset {
          displayName
        }
        emails {
          ...emailCheck
        }
        operationGroup {
          id
          operations {
            id
          }
        }
        tags {
          id
          code
          libelle
          isPermanent
        }
      }
    }
  }
  ${emailCheckFragment}
`;

const fireOperationStateTransitionTriggerForOperationsList = gql`
  mutation fireOperationStateTransitionTriggerForOperationsList(
    $operationIds: [Int!]!
    $trigger: OperationStateTransitionTrigger!
  ) {
    fireOperationStateTransitionTriggerForOperationsList(operationIds: $operationIds, trigger: $trigger) {
      ...operationDispatched
    }
  }
  ${operationDispatchedFragment}
`;

@Component({
  selector: 'app-operation-dashboard',
  templateUrl: './operations-dashboard.component.html',
  styleUrls: ['./operations-dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class OperationsDashboardComponent {
  @ViewChild(OperationsTableComponent)
  private operationTable: OperationsTableComponent;

  private shouldRefreshFromNetwork = false;

  @Input() refreshFromNetworkOnLoad = false;

  @Input()
  columnSelection: OperationDashboardColumn[] = [];

  @Input() allowedActions: OperationSelectionActionType[] | null = null;
  @Input() showCheckbox: (operation: Operation) => boolean = () => true;

  @Input()
  filterSelection: DashboardFilterEnum[] = [];
  @Input()
  defaultQueryFilter: OperationFilterInput = {};

  @Input() preSelectedGestionnaireId: string | undefined;

  @Input() produits: Produit[];
  @Input() natureOperations: NatureOperation[];
  @Input() gestionnaires: BackOfficeMember[];
  @Input() enveloppes: EnveloppeProduit[] = [];

  @Input() withSelects: boolean = false;
  @Input() withCSVExport: boolean = false;
  @Input() isDispatcher: boolean = false;
  @Input() dashboardKey: string;

  // input can be used to disable automatic fetch at loading
  @Input() noFetch: boolean = false;

  // used only to acces to enumns values and handle the filters
  operationDashboardFilterEnum = DashboardFilterEnum;

  @Output()
  onSelectedOperation = new EventEmitter<Operation>();
  @Output()
  onDispatchedOperationsEvent = new EventEmitter();
  @Output()
  onTotalCount = new EventEmitter<number>();

  operations: Operation[] = [];

  dataSource = new MatTableDataSource<Operation>([]);
  paginationParams: PaginationParams = DEFAULT_PAGINATION_PARAMS;
  requestResult: { data: OperationsPaginatedCollectionSegment; loading: boolean } | undefined;

  tableSortCriteria: OperationSortInput | undefined;
  requestFilter: OperationFilterInput | undefined;

  selectionActions: OperationSelectionActionConfig[] = [
    { type: OperationSelectionActionType.Dispatch, label: 'Affecter', icon: 'contact_page' },
    { type: OperationSelectionActionType.EmailConsultant, label: 'Envoyer un email', icon: 'email' },
    { type: OperationSelectionActionType.Terminate, label: 'Terminer les opérations', icon: 'done' },
    { type: OperationSelectionActionType.refuse, label: 'Refuser', icon: 'close' },
    { type: OperationSelectionActionType.cancel, label: 'Annuler', icon: 'cancel' },
  ];
  filterInitialized: boolean = false;
  constructor(
    private queryManager: QueryManagerService,
    private dialogService: OuiDialogService,
    private snackbarService: OuiSnackbarService,
    private ouiDialog: OuiDialogService,
    private userParametersService: UserParametersService
  ) {}

  ngOnInit(): void {
    this.shouldRefreshFromNetwork = this.refreshFromNetworkOnLoad;
  }

  onRowClick(operation: Operation) {
    this.onSelectedOperation.emit(operation);
  }

  onExportClick() {
    const filter = this.requestFilter;
    const modalRef = this.ouiDialog.openDialog(ExcelExportComponent, { queryFilter: filter }, 'auto', 'auto');
  }

  onTableSortCriteriaChange(newCriteria: OperationSortInput | undefined) {
    this.tableSortCriteria = newCriteria;
    this.getAllOperations();
  }

  getAllOperations(forceNetwork = this.shouldRefreshFromNetwork) {
    if (!this.noFetch && this.requestFilter) {
      this.shouldRefreshFromNetwork = false;
      let requestSortCriteria = this.buildRequestSortCriteria();
      const requestVariables = { ...this.paginationParams, filter: this.requestFilter, order: requestSortCriteria };

      this.requestResult = {
        loading: true,
        data: { items: [], totalCount: 0, pageInfo: { hasNextPage: false, hasPreviousPage: false } },
      };

      this.queryManager
        .query<{ operationsPaginated: OperationsPaginatedCollectionSegment }>({
          query: ALLOPERATIONTABLE,
          variables: requestVariables,
          fetchPolicy: forceNetwork ? 'network-only' : 'cache-first',
        })
        .subscribe(({ loading, data }) => {
          this.requestResult = { loading, data: data.operationsPaginated };
          this.operations = data.operationsPaginated.items ?? [];
          if (data.operationsPaginated.items && data.operationsPaginated.totalCount) {
            this.onTotalCount.emit(data.operationsPaginated.totalCount);
          } else {
            this.onTotalCount.emit(0);
          }
        });
    }
  }

  onSelectionActionClick(action: OperationSelectionAction) {
    const operationIds = action.operationIds;
    switch (action.type) {
      case OperationSelectionActionType.Dispatch:
        this.onDispatchedOperations(operationIds);
        break;
      case OperationSelectionActionType.EmailConsultant:
        this.onSendConsultantEmailClick(operationIds);
        break;
      case OperationSelectionActionType.Terminate:
        this.onTerminateOperations(operationIds);
        break;
      case OperationSelectionActionType.refuse:
        this.onRefuseOperations(operationIds);
        break;
      case OperationSelectionActionType.cancel:
        this.onCancelOperations(operationIds);
        break;
      default:
        break;
    }
  }

  async onRefuseOperations(operationIds: number[]) {
    const modalRef = this.ouiDialog.openDialog(
      RefuseCancelOperationModalComponent,
      {
        operationIds: operationIds,
        isReject: true,
      },
      'auto',
      '473px'
    );
    modalRef.afterClosed().subscribe((refusedOperations: Operation[]) => {
      if (refusedOperations && refusedOperations.length > 0) {
        const snackbarMessage =
          refusedOperations.length > 1 ? 'Les opérations ont été refusées' : `L'opération a été refusée`;
        this.snackbarService.open(snackbarMessage, 'success');
        this.operationTable.unselectAll();
        this.requestResult = undefined;
        this.getAllOperations(true);
      }
    });
  }

  onCancelOperations(operationIds: number[]) {
    const modalRef = this.ouiDialog.openDialog(
      RefuseCancelOperationModalComponent,
      {
        operationIds: operationIds,
        isReject: false,
      },
      'auto',
      '473px'
    );
    modalRef.afterClosed().subscribe((cancelledOperations: Operation[]) => {
      if (cancelledOperations && cancelledOperations.length > 0) {
        const snackbarMessage =
          cancelledOperations.length > 1 ? 'Les opérations ont été annulées' : `L'opération a been annulée`;
        this.snackbarService.open(snackbarMessage, 'success');
        this.operationTable.unselectAll();
        this.requestResult = undefined;
        this.getAllOperations(true);
      }
    });
  }

  onTerminateOperations(operationIds: number[]) {
    const modalRef = this.dialogService.openDialog(
      ConfirmationSimpleComponent,
      {
        operation: operationIds,
        title: 'Terminer les opérations',
        message:
          'En cliquant sur “Valider”, vos opérations seront historisées et vous ne pourrez plus revenir en arrière.',
        validateButtonLabel: 'Valider',
      },
      'auto',
      '448px'
    );

    modalRef.afterClosed().subscribe((isValidated: boolean) => {
      if (isValidated) {
        this.queryManager
          .mutate({
            mutation: fireOperationStateTransitionTriggerForOperationsList,
            variables: {
              operationIds: operationIds,
              trigger: OperationStateTransitionTrigger.PartenaireValidateOperation,
            },
          })
          .subscribe(result => {
            if (result) {
              const snackbarMessage =
                operationIds.length > 1 ? 'Les opérations ont été terminées' : `L'opération a été terminée`;
              this.snackbarService.open(snackbarMessage, 'success', 5000, {
                horizontal: 'left',
                vertical: 'bottom',
              });
              this.operationTable.unselectAll();
              this.requestResult = undefined;
              this.onDispatchedOperationsEvent.emit();
              this.getAllOperations(true);
            }
          });
      }
    });
  }

  onDispatchedOperations(dispatchedOperationIds: number[]) {
    if (dispatchedOperationIds && dispatchedOperationIds.length > 0) {
      const modalRef = this.dialogService.openDialog(
        OperationsDispatcherComponent,
        {
          operations: this.operations.filter(operation => dispatchedOperationIds.includes(operation.id)),
          operationIds: dispatchedOperationIds,
          isSingleDispatch: false,
        },
        'auto',
        '1280px'
      );

      modalRef.afterClosed().subscribe((result: Operation[]) => {
        if (!result || result.length === 0) {
          return;
        }

        this.snackbarService.open(this.getDispatchedOperationsMessage(dispatchedOperationIds), 'success', 5000, {
          horizontal: 'left',
          vertical: 'bottom',
        });
        this.operationTable.unselectAll();
        this.requestResult = undefined;
        this.onDispatchedOperationsEvent.emit();
        this.getAllOperations(true);
      });
    }
  }
  onSendConsultantEmailClick(operationIds: number[]) {
    const modalRef = this.ouiDialog.openDialog(
      NotifyMailConsultantComponent,
      {
        operationIds: operationIds,
      },
      'auto',
      '744px'
    );
    modalRef.afterClosed().subscribe((isValidated: boolean) => {
      if (isValidated) {
        this.snackbarService.open('Email(s) bien envoyé(s)', 'success', 5000, {
          horizontal: 'left',
          vertical: 'bottom',
        });
      }
    });
  }
  private getDispatchedOperationsMessage(dispatchedOperationIds: number[]) {
    if (dispatchedOperationIds.length === 1) {
      return "L'opération a été affectée";
    }
    return `Les ${dispatchedOperationIds.length} opérations ont été affectées`;
  }

  buildRequestSortCriteria(): OperationSortInput[] {
    var result = [];
    if (this.tableSortCriteria) {
      result.push(this.tableSortCriteria);
    } else {
      // default sort by date modification if no sort criteria is set
      result.push({ dateModification: SortEnumType.Desc });
    }

    // add sort by id as second sort criteria to ensure determinism
    result.push({ id: SortEnumType.Desc });

    return result;
  }

  async onDeleteDraftClick(operation: Operation) {
    const dialogRef = this.dialogService.openDialog(DeleteDraftConfirmComponent, { operation }, 'auto', '448px');
    dialogRef.afterClosed().subscribe(result => {
      if (result > 0) {
        this.getAllOperations(true);
        this.snackbarService.open('Opération supprimée', 'success', 5000, {
          horizontal: 'left',
          vertical: 'bottom',
        });
      }
    });
  }

  onFilterChanged(event: OperationFilterInput) {
    this.requestFilter = event;

    if (!this.userParametersService.getUserParam(this.dashboardKey, DashboardFilterEnum.Investisseur.toString())) {
      this.getAllOperations();
    } else if (this.filterInitialized) {
      this.getAllOperations();
    }
  }

  /**
   * This method is called when the filter is initialized.
   * It sets the `filterInitialized` property to `true`.
   * This is to prevent results from longer, non-filtered requests from overwriting the filtered results.
   */
  onFilterInitialized() {
    this.filterInitialized = true;
  }

  onRefreshRequested() {
    this.shouldRefreshFromNetwork = true;
  }
}
